/**
 * @file ContentCard.tsx
 * @description This file defines the ContentCard component, a versatile card used to display content previews.
 * It supports multiple variants for different layouts and contexts.
 */
import { blogger_v3 } from "googleapis"
import { useGo, useTranslation } from "@refinedev/core"
import dayjs from "dayjs"
import ReactHtmlParser from 'react-html-parser';
import { sha256 } from "@utils/crypto";
import { useEffect, useState } from "react";

/**
 * Defines the visual variations of the ContentCard.
 */
export type ContentCardVariant =
    'responsive' | // Adapts to container size, showing a side-by-side layout on larger screens.
    'default' |    // A standard card layout.
    'short' |      // A compact, horizontal layout for lists.
    'large' |      // A larger, horizontal layout for featured content.
    'hero'         // A full-bleed card for primary, hero content.

/**
 * Props for the ContentCard component.
 */
export interface ContentCardProps {
    post: blogger_v3.Schema$Post // The post data to display.
    className?: string // Additional classes for the root container.
    cardClassName?: string // Additional classes for the card element.
    bodyClassName?: string // Additional classes for the card body.
    children?: React.ReactNode // Custom children to render inside the card.
    variant?: ContentCardVariant // The card variant to use.
}

/**
 * A predefined color palette for generating consistent label colors.
 */
const colorPalette = [
    '#f44336',
    '#e81e63',
    '#9c27b0',
    '#673ab7',
    '#3f51b5',
    '#2196f3',
    '#03a9f4',
    '#00bcd4',
    '#009688',
    '#4caf50',
    '#8bc34a',
    '#cddc39',
    '#4D470E',
    '#ffc107',
    '#ff9800',
    '#ff5722',
]

/**
 * A versatile card component for displaying content previews.
 * It handles different visual styles through variants and dynamically adjusts its layout.
 */
export function ContentCard({
    post,
    className,
    cardClassName,
    bodyClassName,
    children,
    variant = "default",
    ...rest
}: ContentCardProps) {
    // Hooks for translation and navigation.
    const { translate } = useTranslation()
    const go = useGo()

    // State for storing post labels with their assigned colors.
    const [labelsWithColors, setLabelsWithColors] = useState<{ label: string, color: string }[]>([])
    
    // Extracts the first image from the post, if available.
    const image = post.images?.[0]?.url

    // Parses the post's HTML content to extract a plain text description.
    let description = ""
    ReactHtmlParser(post.content!, {
        transform: (node) => {
            // logger.debug(node)
            // if (node.type === 'tag' && node.name !== 'p') {
            //     return null
            // }

            if (node.type == 'text') {
                description += node.data
            }
        }
    })[0]

    // logger.debug(description)

    // Effect to calculate a consistent color for each post label from the palette.
    useEffect(() => {
        const calcColors = async () => {
            post.labels && setLabelsWithColors(await Promise.all(post.labels.map(async label => {
                // Generate a hash from the label to pick a color, ensuring consistency.
                const hash = await sha256(label)
                const i = Number('0x' + hash.slice(0, 4)) >> 12
                const color = colorPalette[i]
                return {
                    label,
                    color
                }
            })))
        }

        calcColors()
    }, [post.labels])

    // A placeholder to display when a post has no image.
    const ImagePlaceholder = <span className="uppercase w-auto h-auto text-3xl font-bold border-base-content border-2 px-2 font-serif group-hover:scale-105 transition-all duration-100 ease-in-out my-16 md:my-0">T</span>

    return <div
        // Root container with responsive container query for the 'responsive' variant.
        className={`min-w-full ${variant == 'responsive' ? '@container' : ''} ${className ?? ''}`}
        onClick={() => go({ to: { action: 'show', resource: 'content', id: post.id! } })}
        {...rest}>
        <div
            // The main card element with styles adapting to the chosen variant.
            className={`h-full w-full card no-prose group cursor-pointer 
                ${variant == "default" && (image ? '' : 'bg-base-200 hover:bg-black/10')} 
                ${variant == "responsive" && `@2xl:card-side @2xl:bg-base-100 ${image ? '' : 'bg-base-200 hover:bg-black/10'}`} 
                ${["short", "large"].includes(variant) && 'card-side bg-base-100'} 
                ${variant == 'hero' && 'image-full before:bg-transparent! before:opacity-30! before:bg-linear-to-b before:from-transparent before:to-black'}
                ${cardClassName ?? ''}
                `}
        >
            {/* Card figure for the image, with visibility and size based on the variant. */}
            <figure className={`bg-base-200
            ${variant == 'default' && `h-56 w-full ${image ? '' : 'hidden!'}`}
            ${variant == 'responsive' && `h-56 w-full @2xl:h-64 @2xl:min-w-96 @2xl:max-w-96 ${image ? '' : 'hidden! @2xl:flex!'}`}
            ${variant == 'large' && 'h-64 min-w-96 max-w-96'}
            ${variant == 'short' && 'h-24 min-w-36 max-w-36'}            
            `}>
                {image ? <img
                    className="w-full h-full group-hover:scale-105 transition-all duration-100 ease-in-out object-cover"
                    src={post.images?.[0].url}
                /> : ImagePlaceholder
                }
            </figure>
            {/* Card body with padding adjusted based on the variant. */}
            <div className={`card-body flex flex-row gap-4 
        ${variant == "default" && (image ? '' : 'p-6')} 
        ${variant == "responsive" && `@2xl:p-8 ${image ? '' : 'p-6'}`} 
        ${variant == "short" && 'px-4'} 
        ${variant == "hero" && 'p-6'} 
        ${variant == "large" && 'p-8'} 
        ${bodyClassName ?? ''}`}>
                <div className={`h-full w-full flex flex-col 
                    ${['default', 'responsive'].includes(variant) && (image ? '' : 'justify-center')}
                    ${variant == "hero" && 'justify-end'}
                `}>
                    {/* Labels section, shown for specific variants. */}
                    {["default", "responsive", "large", 'hero'].includes(variant) && post.labels && <div className="flex flex-wrap overflow-x-auto gap-2 mb-4 line-clamp-1">
                        {labelsWithColors.map(l => <div key={l.label} className={`uppercase font-light flex items-center
                    ${variant == 'hero' && 'text-white'}`} >
                            <span className="inline-block mr-1 w-2 h-2 rounded-full bg-primary" style={{ backgroundColor: l.color }}></span>{l.label}</div>
                        )}
                    </div>}
                    {/* Card title with text wrapping and clamping based on the variant. */}
                    <div className="card-title text-wrap break-words">
                        <span className={`transition-all duration-100 ease-in-out
                        ${variant == "default" && "line-clamp-3 text-2xl"} 
                        ${variant == "responsive" && "line-clamp-3 text-2xl @2xl:line-clamp-3 @2xl:text-xl"} 
                        ${variant == "large" && "line-clamp-3 text-xl"}
                        ${variant == "short" && "line-clamp-2 text-base"}
                        ${variant == "hero" && "line-clamp-2 text-3xl md:text-5xl text-white"}
                    `}> {post.title!}</span >
                    </div>
                    {/* Custom children can be injected here. */}
                    {children}
                    {/* Card actions, including description and publication date. */}
                    <div className='card-actions'>
                        {/* Description with visibility and line clamping based on the variant. */}
                        <div className={`text-balance
                    ${variant == "default" && `line-clamp-4 ${image ? 'hidden' : ''}`}
                    ${variant == "responsive" && `line-clamp-4 @2xl:line-clamp-3 ${image ? 'hidden' : ''}`}
                    ${["short", "hero"].includes(variant) && 'hidden'}
                    ${variant == "large" && 'line-clamp-3'}
                    `}>{description.slice(0, 500).trim()}</div>

                        {/* Publication date, hidden on the 'hero' variant. */}
                        <div className={`flex items-center grow font-light 
                    ${variant == "default" && 'mt-4'}  
                    ${variant == "responsive" && 'mt-auto'}  
                    ${variant == "short" && 'text-sm'}
                    ${variant == "hero" && 'hidden'}
                    `}>
                            <span className="">{`${translate("page.content.published")} • ${dayjs(post.published).format('MMM D')}`}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
}
